import { buildLegEditSnapshot } from "../../../utils/legUpdateStatus";

/**
 * Form state for the existing leg create modal, opened from the jobs v2 workspace.
 * Add defaults follow the classic legs card. Edit state follows the classic leg table
 * so mileage, driver, and appointment save the same way.
 */

/**
 * Blank leg form. Origin continues from the previous leg's destination when one exists.
 * @param {object|null|undefined} job
 * @returns {object}
 */
export function createLegFormDefaults(job) {
  const legs = Array.isArray(job?.legs) ? job.legs : [];
  const origin = legs.length ? legs[legs.length - 1]?.route?.destination || null : null;

  return {
    job,
    client: job?.client,
    useLegFlatRate: false,
    origin,
    orderNumber: null,
    loadNumber: null,
    loadType: null,
    destination: null,
    weight: 0,
    driver: {},
    appointmentTime: null,
    appointmentDate: null,
    waitEnd: null,
    waitStart: null,
    waitTime: false,
    totalWaitTime: 0,
    baseCharge: 0,
    fuelSurcharge: 0,
    otherCharges: 0,
    dropCharge: 0,
    useCustomFuelSurcharge: false,
    useCustomBaseCharge: false,
    useCustomDropCharge: false,
    useCustomOtherCharges: false,
    totalMiles: 0,
    totalCharges: 0,
    hasHandoff: null,
    isHandoff: false,
    edit: false,
  };
}

/**
 * Return leg: last destination back to the first origin, marked empty.
 * @param {object|null|undefined} job
 * @returns {object}
 */
export function createReturnLegForm(job) {
  const legs = Array.isArray(job?.legs) ? job.legs : [];
  const origin = legs.length ? legs[legs.length - 1]?.route?.destination || null : null;
  const destination = legs.length ? legs[0]?.route?.origin || null : null;

  return {
    ...createLegFormDefaults(job),
    origin,
    destination,
    loadType: "Empty",
  };
}

/**
 * Decide whether the edit form should open in custom mileage mode.
 * Mirrors the classic legs table so a shared route distance is not treated as this leg's miles.
 * @param {object} leg
 * @returns {{
 *   totalMiles: number,
 *   mileageSelectionType: string,
 *   customMileage: number|null,
 *   useCustomMileage: boolean,
 *   defaultMileage: number,
 *   billedMiles: number|null,
 * }}
 */
export function resolveLegEditMileage(leg) {
  const route = leg?.route || {};
  const normalizedSelection = String(leg?.mileageSelectionType || "")
    .toLowerCase()
    .trim();
  const isExplicitDefault = normalizedSelection === "defaultmileage";
  const selectionIndicatesCustom = [
    "billedmileage",
    "billedmiles",
    "custominput",
    "currentmileage",
  ].includes(normalizedSelection);
  const explicitDefaultMileage = route?.defaultMileage ?? route?.mileage ?? null;
  const legacyBaselineMileage = route?.mileage ?? route?.defaultMileage ?? null;
  const comparisonBaselineMileage =
    normalizedSelection === "" ? legacyBaselineMileage : explicitDefaultMileage;
  const snapshotMiles =
    leg?.mileageUsed && leg.mileageUsed > 0
      ? leg.mileageUsed
      : leg?.totalMiles && leg.totalMiles > 0
        ? leg.totalMiles
        : null;
  const baseTotalMiles = snapshotMiles ?? comparisonBaselineMileage ?? 0;
  const shouldUseCustomMileage =
    selectionIndicatesCustom ||
    (!isExplicitDefault &&
      normalizedSelection === "" &&
      comparisonBaselineMileage != null &&
      snapshotMiles != null &&
      snapshotMiles > 0 &&
      snapshotMiles !== comparisonBaselineMileage);
  const resolvedCustomMileage = shouldUseCustomMileage
    ? snapshotMiles ?? route?.customMileage ?? route?.billedMiles ?? baseTotalMiles
    : null;
  const resolvedTotalMiles = shouldUseCustomMileage ? resolvedCustomMileage : baseTotalMiles;

  return {
    totalMiles: resolvedTotalMiles,
    mileageUsed: resolvedTotalMiles,
    mileageSelectionType: isExplicitDefault
      ? "defaultMileage"
      : shouldUseCustomMileage
        ? leg?.mileageSelectionType || "billedMileage"
        : "defaultMileage",
    customMileage: resolvedCustomMileage,
    useCustomMileage: shouldUseCustomMileage,
    defaultMileage: explicitDefaultMileage ?? resolvedTotalMiles,
    billedMiles: route?.billedMiles ?? resolvedCustomMileage,
  };
}

/**
 * Form state for editing one existing leg.
 * @param {object} leg
 * @param {object|null|undefined} job
 * @returns {object}
 */
export function createLegEditForm(leg, job) {
  const record = leg || {};
  const route = record.route || {};
  return {
    ...record,
    ...resolveLegEditMileage(record),
    _editSnapshot: buildLegEditSnapshot(record),
    client: job?.client,
    origin: route.origin,
    destination: route.destination,
    appointmentDate: record.appointmentDate,
    appointmentTime: record.appointment?.time,
    totalWaitTime: record.waitTime?.total,
    waitStart: record.waitTime?.start,
    waitEnd: record.waitTime?.end,
    base: job?.base || job?.baseRate,
    fuelRate: job?.fuelRate,
    orderNumber: record.orderNumber,
    useLegFlatRate: Boolean(job?.useFlatRate || record.useLegFlatRate),
    job,
    edit: true,
  };
}
